import React, { useEffect, useState, useCallback } from 'react';
import { Table, Button, Modal, Form, Input, InputNumber, Select, message, Popconfirm, Space, Tag, Checkbox } from 'antd';
import { PlusOutlined, DeleteOutlined, EditOutlined, ShopOutlined } from '@ant-design/icons';
import { apiService } from '../services/api';
import { User, CreateUserRequest, UserRole, UpdateUserRequest, UpdateMerchantConfig } from '../types/auth';
import { isAdmin } from '../utils/auth';
import { useNavigate } from 'react-router-dom';

const { Option } = Select;

const Users: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [isMerchantModalVisible, setIsMerchantModalVisible] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [editingMerchantUser, setEditingMerchantUser] = useState<User | null>(null);
  const [availableAuthorities, setAvailableAuthorities] = useState<string[]>([]);
  const [loadingAuthorities, setLoadingAuthorities] = useState(false);
  const [loadingMerchant, setLoadingMerchant] = useState(false);
  const [loadingMerchantConfig, setLoadingMerchantConfig] = useState(false);
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();
  const [merchantForm] = Form.useForm();
  const navigate = useNavigate();

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiService.getUsers();
      // Ensure data is always an array
      setUsers(Array.isArray(data) ? data : []);
    } catch (error: any) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        navigate('/login');
      } else {
        message.error('Failed to load users');
      }
      // Set empty array on error
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    if (!isAdmin()) {
      navigate('/dashboard');
      return;
    }
    loadUsers();
  }, [navigate, loadUsers]);

  const handleAddUser = () => {
    setIsModalVisible(true);
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
    form.resetFields();
  };

  const handleCreateUser = async (values: CreateUserRequest) => {
    try {
      await apiService.createUser(values);
      message.success('User created successfully');
      setIsModalVisible(false);
      form.resetFields();
      loadUsers();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to create user');
    }
  };

  const handleDeleteUser = async (userId: number) => {
    try {
      await apiService.deleteUser(userId);
      message.success('User deleted successfully');
      loadUsers();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to delete user');
    }
  };

  const loadAuthorities = useCallback(async () => {
    setLoadingAuthorities(true);
    try {
      const authorities = await apiService.getUserAuthorities();
      setAvailableAuthorities(authorities || []);
    } catch (error: any) {
      message.error('Failed to load available authorities');
      // Fallback to empty array or default values
      setAvailableAuthorities([]);
    } finally {
      setLoadingAuthorities(false);
    }
  }, []);

  const handleEditAuthorities = async (user: User) => {
    setEditingUser(user);
    // Загружаем актуальные authorities при открытии модального окна
    await loadAuthorities();
    editForm.setFieldsValue({
      authorities: user.authorities || []
    });
    setIsEditModalVisible(true);
  };

  const handleEditModalCancel = () => {
    setIsEditModalVisible(false);
    setEditingUser(null);
    editForm.resetFields();
  };

  const handleUpdateAuthorities = async (values: { authorities: string[] }) => {
    if (!editingUser) return;
    
    try {
      const request: UpdateUserRequest = {
        id: editingUser.id,
        authorities: values.authorities || []
      };
      await apiService.updateUser(request);
      message.success('User authorities updated successfully');
      setIsEditModalVisible(false);
      setEditingUser(null);
      editForm.resetFields();
      loadUsers();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to update user authorities');
    }
  };

  const handleEditMerchant = async (user: User) => {
    setEditingMerchantUser(user);
    setIsMerchantModalVisible(true);
    setLoadingMerchantConfig(true);
    
    try {
      const merchantName = (user as any).merchantName || user.username || '';
      const configResponse = await apiService.getMerchantConfig(merchantName);
      
      merchantForm.setFieldsValue({
        daysUntilNextEvent: configResponse.data?.daysUntilNextEvent || 0
      });
    } catch (error: any) {
      message.error('Failed to load merchant config');
      merchantForm.setFieldsValue({
        daysUntilNextEvent: 0
      });
    } finally {
      setLoadingMerchantConfig(false);
    }
  };

  const handleMerchantModalCancel = () => {
    setIsMerchantModalVisible(false);
    setEditingMerchantUser(null);
    merchantForm.resetFields();
  };

  const handleUpdateMerchant = async (values: { daysUntilNextEvent: number }) => {
    if (!editingMerchantUser) return;
    
    setLoadingMerchant(true);
    try {
      const merchantName = (editingMerchantUser as any).merchantName || editingMerchantUser.username || '';
      const merchantData: UpdateMerchantConfig = {
        name: merchantName,
        config: {
          daysUntilNextEvent: values.daysUntilNextEvent
        }
      };
      
      const response = await apiService.updateMerchant(merchantData);
      
      // Если ответ успешный (200 OK), считаем операцию успешной
      // Проверяем наличие поля success только если оно есть, иначе считаем успехом
      if (response && (response.success !== false)) {
        message.success(response.msg || 'Merchant updated successfully');
        setIsMerchantModalVisible(false);
        setEditingMerchantUser(null);
        merchantForm.resetFields();
        loadUsers();
      } else if (response && response.success === false) {
        message.error(response.msg || 'Failed to update merchant');
      } else {
        // Если нет поля success, но запрос выполнен успешно (200 OK), считаем успехом
        message.success('Merchant updated successfully');
        setIsMerchantModalVisible(false);
        setEditingMerchantUser(null);
        merchantForm.resetFields();
        loadUsers();
      }
    } catch (error: any) {
      console.log('Update merchant error:', error);
      console.log('Error response:', error.response);
      
      // Handle structured error responses (success=false in response body)
      if (error.response?.data && typeof error.response.data === 'object') {
        const errorData = error.response.data;
        if ('success' in errorData && 'msg' in errorData && errorData.success === false) {
          message.error(errorData.msg || 'Failed to update merchant');
          return;
        }
      }
      
      const errorMsg = error.response?.data?.msg || error.response?.data?.message || error.message || 'Failed to update merchant';
      message.error(errorMsg);
    } finally {
      setLoadingMerchant(false);
    }
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
    },
    {
      title: 'Username',
      dataIndex: 'username',
      key: 'username',
    },
    {
      title: 'Role',
      dataIndex: 'role',
      key: 'role',
      render: (role: UserRole) => (
        <span style={{ 
          color: role === 'admin' ? '#1890ff' : '#52c41a',
          fontWeight: 'bold' 
        }}>
          {role.toUpperCase()}
        </span>
      ),
    },
    {
      title: 'Authorities',
      dataIndex: 'authorities',
      key: 'authorities',
      render: (authorities: string[] | undefined) => {
        if (!authorities || authorities.length === 0) {
          return <Tag color="default">No authorities</Tag>;
        }
        return (
          <Space size={[8, 8]} wrap>
            {authorities.map((auth) => (
              <Tag key={auth} color="blue">{auth}</Tag>
            ))}
          </Space>
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: User) => (
        <Space size="middle">
          <Button 
            type="link" 
            icon={<EditOutlined />}
            size="small"
            onClick={() => handleEditAuthorities(record)}
          >
            Edit Authorities
          </Button>
          {record.role === 'merchant' && (
            <Button 
              type="link" 
              icon={<ShopOutlined />}
              size="small"
              onClick={() => handleEditMerchant(record)}
            >
              Edit Merchant
            </Button>
          )}
          <Popconfirm
            title="Are you sure you want to delete this user?"
            onConfirm={() => handleDeleteUser(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button 
              type="text" 
              danger 
              icon={<DeleteOutlined />}
              size="small"
            >
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  if (!isAdmin()) {
    return null;
  }

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2>Users Management</h2>
        <Button 
          type="primary" 
          icon={<PlusOutlined />} 
          onClick={handleAddUser}
        >
          Add User
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={users}
        loading={loading}
        rowKey="id"
        pagination={{ pageSize: 10 }}
      />

      <Modal
        title="Add New User"
        open={isModalVisible}
        onCancel={handleModalCancel}
        footer={null}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleCreateUser}
        >
          <Form.Item
            label="Username"
            name="username"
            rules={[
              { required: true, message: 'Please input username!' },
              { min: 3, message: 'Username must be at least 3 characters!' }
            ]}
          >
            <Input placeholder="Enter username" />
          </Form.Item>

          <Form.Item
            label="Password"
            name="password"
            rules={[
              { required: true, message: 'Please input password!' },
              { min: 6, message: 'Password must be at least 6 characters!' }
            ]}
          >
            <Input.Password placeholder="Enter password" />
          </Form.Item>

          <Form.Item
            label="Role"
            name="role"
            rules={[{ required: true, message: 'Please select role!' }]}
          >
            <Select placeholder="Select role">
              <Option value="admin">Admin</Option>
              <Option value="merchant">Merchant</Option>
              <Option value="manager">Manager</Option>
            </Select>
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={handleModalCancel}>
                Cancel
              </Button>
              <Button type="primary" htmlType="submit">
                Create User
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={`Edit Authorities - ${editingUser?.username}`}
        open={isEditModalVisible}
        onCancel={handleEditModalCancel}
        footer={null}
        width={600}
      >
        <Form
          form={editForm}
          layout="vertical"
          onFinish={handleUpdateAuthorities}
        >
          <Form.Item
            label="Authorities"
            name="authorities"
          >
            {loadingAuthorities ? (
              <div style={{ textAlign: 'center', padding: '20px' }}>
                Loading authorities...
              </div>
            ) : (
              <Checkbox.Group style={{ width: '100%' }}>
                <Space direction="vertical" style={{ width: '100%' }}>
                  {availableAuthorities.length > 0 ? (
                    availableAuthorities.map((auth) => (
                      <Checkbox key={auth} value={auth}>
                        {auth}
                      </Checkbox>
                    ))
                  ) : (
                    <div style={{ color: '#999', fontStyle: 'italic' }}>
                      No authorities available
                    </div>
                  )}
                </Space>
              </Checkbox.Group>
            )}
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={handleEditModalCancel}>
                Cancel
              </Button>
              <Button type="primary" htmlType="submit">
                Update Authorities
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={`Edit Merchant Config - ${editingMerchantUser?.username}`}
        open={isMerchantModalVisible}
        onCancel={handleMerchantModalCancel}
        footer={null}
        width={600}
      >
        {loadingMerchantConfig ? (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            Loading merchant config...
          </div>
        ) : (
          <Form
            form={merchantForm}
            layout="vertical"
            onFinish={handleUpdateMerchant}
          >
            <Form.Item
              label="Days Until Next Event"
              name="daysUntilNextEvent"
              rules={[
                { required: true, message: 'Days until next event is required' },
                { type: 'number', min: 0, message: 'Days must be a positive number' }
              ]}
            >
              <InputNumber 
                min={0}
                placeholder="Enter days until next event" 
                disabled={loadingMerchant}
                style={{ width: '100%' }}
              />
            </Form.Item>

            <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
              <Space>
                <Button onClick={handleMerchantModalCancel} disabled={loadingMerchant}>
                  Cancel
                </Button>
                <Button type="primary" htmlType="submit" loading={loadingMerchant}>
                  Update Merchant Config
                </Button>
              </Space>
            </Form.Item>
          </Form>
        )}
      </Modal>
    </div>
  );
};

export default Users;